/* istanbul ignore file */

import { error, ValidationError } from 'common/errors';
import { pushErrors } from 'common/gamp';
import completeCheckoutLegacy from 'communicator/external/checkout/integrated/legacy/complete-checkout-legacy';
import { saveFlow, saveMeta, saveProfile, saveUser } from 'communicator/store';
import {
  getAccessToken,
  getApiKey,
  getComplianceResource,
  getConsumerNationalIdentiferData,
  getCorrelationId,
  getDpaTransactionOptions,
  getFullAccessToken,
  getIsFeatureToggleEnabled,
  getSessionId,
  getSrcDpaId,
  getTncEntryInfo,
  getTncStatus,
  getUser,
  getVIntentId,
  getVUserForCreateAccount
} from 'communicator/store/selectors';
import { fetchRevokeIdTokens } from 'communicator/utils/api/collection';
import fetchCreateAccount from 'communicator/utils/api/create-account';
import fetchEnrollCard from 'communicator/utils/api/enroll-card';
import { fetchGetIntent, fetchPutIntent } from 'communicator/utils/api/intent';
import { fetchAddPaymentInstrument } from 'communicator/utils/api/payment-instrument';
import { acceptTnc } from 'communicator/utils/api/tnc';
import { generateAccessTokenFromAuthCode } from 'communicator/utils/api/token';
import { encryptData } from 'communicator/utils/encrypt';
import { isEnrollCardError } from 'communicator/utils/enroll-card-error';
import { getHeader } from 'communicator/utils/fetch';
import generateXAuthCodeFromAccessToken from 'communicator/utils/generate-x-auth-code';
import { getLockoutFlowLegacy } from 'communicator/utils/get-lockout-flow';
import convertIdTokenToAccessToken from 'communicator/utils/id-token';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import { AuthenticationMethod, Consumer } from 'types/emvco';
import { RequiredField } from 'types/utility';
import { CheckoutCommunicatorPayload, CheckoutData } from 'types/visa-sdk/checkout';

export default async function checkoutIntegratedEnrollCardLegacy(
  checkoutData: RequiredField<CheckoutData, 'consumer' | 'encryptedCard'>,
  launchSRCBankApp: (data: AuthenticationMethod) => void,
  launchSRCIssuerOtp: (authenticationMethod: AuthenticationMethod) => void,
  launchCvv: () => void,
  launchDcf: () => void
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  let vIntentId = getVIntentId();
  let xAuthCode: string | null = null;
  let accessTokenState = getAccessToken();

  // If we don't already have a full accessToken (from /recognized or /probe),
  // and there is an idToken in the request, convert it to an accessToken
  if (checkoutData.idToken) {
    if (accessTokenState?.token && !accessTokenState.isPartial) {
      // Revoke idTokens if accessToken is available, fire and forget
      fetchRevokeIdTokens({
        accessToken: accessTokenState.token,
        idTokens: [checkoutData.idToken],
        mark: 'checkout_revoke_id_tokens'
      });
    } else {
      const tokenResponse = await convertIdTokenToAccessToken({
        idToken: checkoutData.idToken,
        marks: { token: 'checkout_access_token_from_id_token' }
      });

      if (!tokenResponse.ok && getLockoutFlowLegacy(tokenResponse)) {
        saveUser({
          accessToken: null,
          isLocked: true
        });

        // START GENAI@GHCOPILOT
        pushErrors('CARD_ADD_FAILED_LEGACY', 'AUTH_INVALID');
        // END GENAI@GHCOPILOT
        return error('AUTH_INVALID');
      }
    }
  }

  accessTokenState = getAccessToken();
  const endMarker = getFullAccessToken()
    ? 'CHECKOUT_INTEGRATED_LEGACY_ENROLL_CARD_RETURNING'
    : 'CHECKOUT_INTEGRATED_LEGACY_ENROLL_CARD_NEW';

  const encryptedConsumer = await encryptData<Consumer>(checkoutData.consumer);
  const apikey = getApiKey();
  const dpaTransactionOptions = getDpaTransactionOptions();
  let user = getUser();
  const isNewUserFlow = !accessTokenState?.token || accessTokenState.isPartial;

  markStart('checkout_integrated_using_legacy_enroll_card');

  const enrollCardResponse = await fetchEnrollCard({
    accessToken: accessTokenState?.isPartial ? undefined : accessTokenState?.token,
    apikey,
    dpaTransactionOptions,
    encryptedBillingAddress: checkoutData.encryptedBillingAddress,
    encryptedCard: checkoutData.encryptedCard,
    encryptedConsumer,
    intentId: vIntentId,
    // Only pass srcCorrelationId if a user is recognized or logged in
    srcCorrelationId: user?.isLoggedInOtpUser || user?.isRecognizedUser ? vIntentId : undefined,
    srcDpaId: getSrcDpaId()
  });

  markEnd('checkout_integrated_using_legacy_enroll_card');

  if (!enrollCardResponse.ok) {
    const err =
      enrollCardResponse.errorObject.reason ||
      String(enrollCardResponse.errorCode) ||
      'CARD_ADD_FAILED';

    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: err
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', err);
    // END GENAI@GHCOPILOT
    return error(err);
  }

  if (isEnrollCardError(enrollCardResponse.data, dpaTransactionOptions)) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', 'ENROLL_CARD_ERROR');
    // END GENAI@GHCOPILOT
    return error('CARD_ADD_FAILED');
  }

  const isLocked = enrollCardResponse.data.maskedConsumer?.status === 'LOCKED';

  /**
   * For logged in otp user who did NOT pass consumer.consumerIdentity in
   * checkout request, use unmasked email from maskedConsumer object for
   * VCO-14074. Per F20000, maskConsumer may not be sent back if no consumer
   * in request, add default value
   */
  const email =
    !isLocked && checkoutData.consumer.consumerIdentity
      ? checkoutData.consumer.consumerIdentity.identityValue
      : (enrollCardResponse.data.maskedConsumer?.maskedConsumerIdentity.maskedIdentityValue ?? '');

  saveUser({
    email,
    isLocked,
    mobileNumber: checkoutData.consumer.mobileNumber
  });

  // For returning user flows, there will already be a vIntentId, otherwise we
  // get intentId from the enrollCardResponse.data.srcCorrelationId. If both are
  // missing, then there is some missing data in the request.
  if (enrollCardResponse.data.srcCorrelationId) {
    vIntentId = enrollCardResponse.data.srcCorrelationId;
    saveMeta({ vIntentId });
  }

  const pendingEvents = enrollCardResponse.data.maskedCard.digitalCardData.pendingEvents;

  const hasPendingEvents = Boolean(
    pendingEvents?.length &&
      !(pendingEvents.length === 1 && pendingEvents[0] === 'PENDING_CONSUMER_IDV')
  );

  if (!vIntentId || hasPendingEvents) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    // START GENAI@GHCOPILOT
    pushErrors(
      'CARD_ADD_FAILED_LEGACY',
      'One or more required parameter missing, please check missing fields'
    );
    // END GENAI@GHCOPILOT

    return error(
      'CARD_ADD_FAILED',
      'One or more required parameter missing, please check missing fields'
    );
  }

  if (getTncStatus() === 'not_accepted') {
    const tncError = error(
      'CARD_ADD_FAILED',
      'One or more required parameter missing, please check missing fields'
    );

    if (!getComplianceResource('TERMS_AND_CONDITIONS')) {
      // START GENAI@GHCOPILOT
      pushErrors('CARD_ADD_FAILED', 'Missing compliance resource: TERMS_AND_CONDITIONS');
      // END GENAI@GHCOPILOT
      return tncError;
    }

    const accessTokenState = getAccessToken();
    const tncEntryInfo = getTncEntryInfo();
    const tncResponse = await acceptTnc({ accessToken: accessTokenState?.token, tncEntryInfo });

    if (!tncResponse.ok) {
      return tncError;
    }

    saveUser({
      accessToken: {
        expiresIn: tncResponse.data.expires_in,
        token: tncResponse.data.access_token,
        isPartial: false
      },
      tnc: {
        status: 'accepted',
        tncEntryInfo
      }
    });
  }

  user = getUser();

  // Recognized users adding a card will always be stepped up, so need to launch DCF
  // regardless of integrated flag
  if (user?.isRecognizedUser || user?.isNonVisaRecognizedUser) {
    const accessToken = getAccessToken()?.token as string;
    saveMeta({
      // appLoaded event waits until this flag is set to return configs to the app
      isCheckoutDataReady: true
    });

    saveFlow({
      addCardInfo: {
        cardSuffix: enrollCardResponse.data.maskedCard.panLastFour,
        enrollCardResponse: enrollCardResponse.data,
        networkId: 'VISA',
        panJWE:
          enrollCardResponse.data.customOutputData.encryptedCard ||
          (checkoutData.encryptedCard as string)
      },
      toRoute: 'enroll-card'
    });

    try {
      // Convert access token to x-auth-code so DCF can have its own access token
      await generateXAuthCodeFromAccessToken({
        accessToken,
        mark: 'checkout_integrated_auth_code_from_access_token'
      });

      launchDcf();
    } catch (err: unknown) {
      // START GENAI@GHCOPILOT
      const sErr =
        err && typeof JSON.stringify(err) === 'string' ? JSON.stringify(err) : 'Unknown error';

      pushErrors('CARD_ADD_FAILED_LEGACY: DCF_LAUNCH_ERROR', sErr);
      // END GENAI@GHCOPILOT
    } finally {
      return {};
    }
  }

  xAuthCode = getHeader(enrollCardResponse, 'x-auth-code') as string;
  const encPaymentInstrument =
    enrollCardResponse.data.customOutputData.encryptedCard || checkoutData.encryptedCard;

  const taxIdData = getConsumerNationalIdentiferData();

  if (!isNewUserFlow) {
    // Returning user add card flow, add PI from enrollCard api to existing account
    const accessToken = getAccessToken()?.token as string;

    markStart('checkout_integrated_add_payment_instrument');
    const addPaymentInstrumentResponse = await fetchAddPaymentInstrument({
      accessToken,
      apikey,
      requestPayload: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        encPaymentInstrument: encPaymentInstrument!,
        intentId: vIntentId,
        mobileNumber: getUser()?.mobileNumber,
        paymentInstrument: {
          paymentType: { cardBrand: 'VISA' }
        }
      }
    });

    markEnd('checkout_integrated_add_payment_instrument');

    if (!addPaymentInstrumentResponse.ok) {
      // TODO: handle POST /pi failure
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'ADD_CARD_FAILURE'
      });

      // START GENAI@GHCOPILOT
      pushErrors('CARD_ADD_FAILED_LEGACY', 'ADD_CARD_FAILURE');
      // END GENAI@GHCOPILOT
      return error('ADD_CARD_FAILURE');
    }

    const selectedCardId = addPaymentInstrumentResponse.data.paymentInstrument.id;

    saveMeta({ selectedCardId });

    markStart('checkout_integrated_put_intent');

    const putIntentResponse = await fetchPutIntent({
      accessToken,
      apikey,
      // TODO: complete PUT intent payload
      requestPayload: {
        assuranceData: checkoutData.assuranceData,
        dfpSessionId: getSessionId(),
        encryptedBillingAddress: checkoutData.encryptedBillingAddress,
        paymentInstrumentId: selectedCardId,
        referenceURL: window.location.href,
        srciTransactionId: getCorrelationId(),
        ...(getIsFeatureToggleEnabled('enableCpfSupportBrazil') && taxIdData)
      },
      vIntentId,
      vsbFlow: checkoutData.vsbFlow || false
    });

    markEnd('checkout_integrated_put_intent', {
      failure: !putIntentResponse.ok,
      reason: putIntentResponse.ok ? undefined : 'CHECKOUT_FAILURE'
    });

    // do not need to return encPaymentInstrument (to be passed into PUT /intent)
    // since we already added the card to the user's account
    return completeCheckoutLegacy(
      { checkoutData, putIntentResponse },
      launchSRCBankApp,
      launchSRCIssuerOtp,
      launchCvv,
      launchDcf,
      null,
      null,
      null,
      null
    );
  }

  // If new user, GET /intent to get PI id, create an account, and get an access token
  const sessionId = getSessionId();

  // The accessToken generated here is restricted in scope. Following create
  // account, we will generate a new access token with unrestricted scope
  const tokenResponseWithPartialScope = await generateAccessTokenFromAuthCode({
    apikey,
    authCode: xAuthCode as string,
    sessionId
  });

  if (!tokenResponseWithPartialScope.ok) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', 'generate access token from auth code failed');
    // END GENAI@GHCOPILOT
    return error('CARD_ADD_FAILED');
  }

  saveUser({
    accessToken: {
      expiresIn: tokenResponseWithPartialScope.data.expires_in,
      isPartial: false,
      token: tokenResponseWithPartialScope.data.access_token
    }
  });

  const accessToken = getAccessToken()?.token as string;
  const vuser = getVUserForCreateAccount();

  markStart('checkout_integrated_get_intent');
  markStart('checkout_integrated_create_account');

  const [getIntentResponse, createAccountResponse] = await Promise.all([
    // GET /intent to get paymentInstrument ID mapping
    fetchGetIntent({ accessToken, apikey, vIntentId }).then(res => {
      markEnd('checkout_integrated_get_intent');
      return res;
    }),
    fetchCreateAccount({ accessToken, apikey, sessionId, vuser }).then(res => {
      markEnd('checkout_integrated_create_account');
      return res;
    })
  ]);

  if (!getIntentResponse.ok) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', 'GET_INTENT_FAILURE or CREATE_ACCOUNT_FAILURE');
    // END GENAI@GHCOPILOT
    return error('CARD_ADD_FAILED');
  }

  // For new user flows, there will be one PI in the paymentInstrument array
  const selectedCardId = getIntentResponse.data.paymentInstrument[0].id;

  saveMeta({ selectedCardId });
  saveProfile({
    shippingAddress: getIntentResponse.data.shippingAddress,
    paymentInstrument: getIntentResponse.data.paymentInstrument
  });

  if (!createAccountResponse.ok) {
    markEnd('CHECKOUT_INTEGRATED_LEGACY_ENROLL_CARD', {
      doFlush: true,
      failure: true,
      reason: 'CREATE_ACCOUNT_FAILURE'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', 'CREATE_ACCOUNT_FAILURE');
    // END GENAI@GHCOPILOT
    return error('CREATE_ACCOUNT_FAILURE');
  }

  // Here we generate a new access token with unrestricted scope. See:
  // https://jira.trusted.visa.com/browse/VCO-16079
  const tokenResponseWithFullScope = await generateAccessTokenFromAuthCode({
    apikey,
    authCode: getHeader(createAccountResponse, 'x-auth-code') as string,
    sessionId
  });

  if (!tokenResponseWithFullScope.ok) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', 'generate access token from auth code failed');
    // END GENAI@GHCOPILOT
    return error('CARD_ADD_FAILED');
  }

  saveUser({
    accessToken: {
      expiresIn: tokenResponseWithFullScope.data.expires_in,
      isPartial: false,
      token: tokenResponseWithFullScope.data.access_token
    }
  });

  markStart('checkout_integrated_put_intent');

  const putIntentResponse = await fetchPutIntent({
    accessToken,
    apikey,
    // TODO: complete PUT intent payload
    requestPayload: {
      assuranceData: checkoutData.assuranceData,
      dfpSessionId: getSessionId(),
      encPaymentInstrument,
      paymentInstrumentId: selectedCardId,
      referenceURL: window.location.href,
      srciTransactionId: getCorrelationId(),
      ...(getIsFeatureToggleEnabled('enableCpfSupportBrazil') && taxIdData)
    },
    vIntentId,
    vsbFlow: checkoutData.vsbFlow || false
  });

  // do not need to return encPaymentInstrument (to be passed into PUT /intent)
  // since we already added the card to the user's account
  const checkoutResponse = await completeCheckoutLegacy(
    { checkoutData, putIntentResponse },
    launchSRCBankApp,
    launchSRCIssuerOtp,
    launchCvv,
    launchDcf,
    null,
    null,
    null,
    null
  );

  if (checkoutResponse.error) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CHECKOUT_FAILURE'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED_LEGACY', 'CHECKOUT_FAILURE');
    // END GENAI@GHCOPILOT
    return error('CHECKOUT_FAILURE');
  }

  markEnd(endMarker, { doFlush: true });

  return checkoutResponse;
}
